import { useEffect, useRef, useState, type FormEvent } from 'react';
import { btnGhost, btnPrimary, inputCls as inputClass } from './styles';
import { Spinner } from '../../ui/Spinner';
import { ApiError } from '../../core/api/http';
import { reauth } from '../adminApi';
type Props = {
  open: boolean;
  /** Moet de code uit de authenticator-app er ook bij? */
  twoFactor: boolean;
  /** Waarom we het vragen, in één zin: "Rollen aanpassen vraagt een extra bevestiging." */
  reason: string;
  onDone: () => void;
  onCancel: () => void;
};

/**
 * "Bevestig dat jij het bent": wachtwoord (en de code, met 2FA aan) voor gevoelige acties zoals
 * rollen, stoppen, sessies en de klantexport. Geldt 15 minuten; daarna vraagt de server het
 * opnieuw (423 `reauth_required`). Native modale dialog: focus blijft erin, Escape = annuleren.
 */
export function ReauthDialog({
  open,
  twoFactor,
  reason,
  onDone,
  onCancel
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setPassword('');
      setCode('');
      setError(null);
      d.showModal();
      passwordRef.current?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      await reauth(twoFactor ? {
        password,
        code: code.replace(/\s/g, '')
      } : {
        password
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Dat lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  return <dialog ref={ref} aria-labelledby="reauth-title" aria-describedby="reauth-text" onCancel={e => {
    e.preventDefault();
    if (!busy) onCancel();
  }} className="w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-line bg-shell p-0 text-ink shadow-xl backdrop:bg-ink/40" data-id="element-5155" data-mp="3p7efh3cjljhq">
      <form onSubmit={submit} className="p-5" noValidate data-id="element-5156" data-mp="18nu74sx8g2aq">
        <h2 id="reauth-title" className="text-[16px] font-medium text-ink" data-id="element-5157" data-mp="18l7u4drkoul4">
          Bevestig dat jij het bent
        </h2>
        <p id="reauth-text" className="mt-2 text-[14px] text-muted" data-id="element-5158" data-mp="21c9cenqkb68v">
          {reason} Daarna kun je een kwartier verder zonder het opnieuw te vragen.
        </p>
        <div className="mt-4 space-y-3" data-id="element-5159" data-mp="26ab2vtqbi7ud">
          <label className="block" data-id="element-5160" data-mp="1rebmzwwyv3eo">
            <span className="text-[13px] text-ink" data-id="element-5161" data-mp="1rdabxsfyagwy">Wachtwoord</span>
            <input ref={passwordRef} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} aria-invalid={Boolean(error)} className={`${inputClass} mt-1`} data-id="element-5162" data-mp="1wcdf17cded2a" />
          </label>
          {twoFactor && <label className="block" data-id="element-5163" data-mp="2z7buusva1ffa">
              <span className="text-[13px] text-ink" data-id="element-5164" data-mp="2u77inf1ej6uv">Code uit je authenticator-app</span>
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} aria-invalid={Boolean(error)} className={`${inputClass} mt-1 tabular-nums tracking-widest`} data-id="element-5165" data-mp="386p69r2yoe2n" />
            </label>}
          {error && <p role="alert" className="text-[13px] text-clay" data-id="element-5166" data-mp="2p72xvctgnpyu">
              {error}
            </p>}
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2" data-id="element-5167" data-mp="2kgcvm8gc038d">
          <button type="button" onClick={onCancel} disabled={busy} className={btnGhost} data-id="element-5168" data-mp="1wr0kh31g1l3f">
            Annuleren
          </button>
          <button type="submit" disabled={busy || !password || twoFactor && code.trim().length < 6} className={btnPrimary} data-id="element-5169" data-mp="1sq8a26gu5a5k">
            {busy && <Spinner data-id="element-5170" data-mp="25ghcqq33jqt9" />}
            Bevestigen
          </button>
        </div>
      </form>
    </dialog>;
}